// src/pages/subscription/PlanListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionPlan, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { PlanFormDialog } from '@/features/subscription/PlanFormDialog';
import { formatMoney, humanizeEnum } from '@/features/subscription/format';

export const PlanListPage = observer(() => {
  const store = useSubscriptionPlan();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    store.ListPlans({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm(t('subscription.deletePlanConfirm'))) return;
    try {
      await store.DeletePlan(itemId);
      store.ListPlans({
        pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
        sort: [], date_range: {} as any, filters: {} as any,
      });
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="sr-only">Subscription Plans</h1>
          <nav className="text-sm">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Plans</span>
          </nav>
        </div>
        <Button onClick={() => setShowCreate(true)}>Create Plan</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Billing Cycle</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Public</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={7} message="No plans found" />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link to={`/subscriptions/plans/${itemId}`} className="text-primary hover:underline">
                        {String(item.name ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>{String(item.slug ?? '--')}</TableCell>
                    <TableCell>{formatMoney(item.price, item.currency)}</TableCell>
                    <TableCell>
                      {item.billing_cycle ? (
                        <Badge variant="primary" size="sm">{humanizeEnum(item.billing_cycle, 'BILLING_CYCLE_')}</Badge>
                      ) : '--'}
                    </TableCell>
                    <TableCell>
                      {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
                    </TableCell>
                    <TableCell>
                      {item.is_public ? <Badge variant="primary" size="sm">Public</Badge> : <Badge variant="secondary" size="sm">Private</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/plans/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(itemId)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <PlanFormDialog
        open={showCreate}
        plan={null}
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          store.ListPlans({
            pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
            sort: [], date_range: {} as any, filters: {} as any,
          });
        }}
      />
    </div>
  );
});

export default PlanListPage;
